import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";
import { Maximize2, Mic, Minimize2 } from "lucide-react";
import { useDictation } from "../../hooks/useDictation";

import type { AbcGameArtifactConfig } from "../../types/chat";
import { N8N_ABC_GAME_URL } from "../../config/n8n";

type Props = {
  artifact: AbcGameArtifactConfig;

  onAudioResponse?: (
    audioBase64?: string | null,
    mimeType?: string | null,
    audioUrl?: string | null
  ) => void | Promise<void>;

  /**
   * Reports the authoritative "Buddy says" text whenever
   * ABC Game produces a new game state.
   *
   * ChatContainer can use this to speak the exact same text
   * through GPT-Live instead of letting Live improvise.
   */
  onGameMessage?: (
    message: string
  ) => void;
};

export type StepAbcGameBubbleHandle = {
  /**
   * Route one realtime-voice transcript into the active ABC Game.
   *
   * Returns true when ABC Game owns/consumes the utterance.
   * Returns false once the game has ended so normal chat can
   * regain voice ownership.
   */
  executeVoiceCommand: (raw: string) => boolean;
};

type Category = "food" | "animals" | "";
type TurnOwner = "user" | "assistant";
type GameStage = "choose_category" | "choose_start_order" | "playing" | "ended";

type GameStatus = "idle" | "playing" | "won" | "lost" | "quit";

type UsedMove = {
  letter: string;
  word: string;
  by: TurnOwner;
  accepted?: boolean;
};

type GameState = {
  category: Category;
  currentLetter: string;
  turnOwner: TurnOwner;
  startOrder: TurnOwner | null;
  usedAnswers: UsedMove[];
  status: GameStatus;
  message: string;
  messageType: "default" | "hint";
  stage: GameStage;
  mistakeCountThisTurn: number;
  hintUsedThisTurn: boolean;
  passUsedThisTurn: boolean;
  startedAt: string | null;
};

const previewState: GameState = {
  category: "animals",
  currentLetter: "A",
  turnOwner: "user",
  startOrder: "user",
  usedAnswers: [],
  status: "playing",
  message: "Great — you start. Name an animal that starts with A.",
  messageType: "default",
  stage: "playing",
  mistakeCountThisTurn: 0,
  hintUsedThisTurn: false,
  passUsedThisTurn: false,
  startedAt: null,
};

function normalizeCategory(value: unknown): Category {
  const s = String(value ?? "").trim().toLowerCase();
  if (s === "food" || s === "foods") return "food";
  if (s === "animal" || s === "animals") return "animals";
  return "";
}

function normalizeTurnOwner(value: unknown): TurnOwner {
  const s = String(value ?? "").trim().toLowerCase();
  return s === "assistant" ? "assistant" : "user";
}

function normalizeStatus(value: unknown): GameStatus {
  const s = String(value ?? "").trim().toLowerCase();
  if (s === "playing") return "playing";
  if (s === "won") return "won";
  if (s === "lost") return "lost";
  if (s === "quit") return "quit";
  if (s === "complete") return "won";
  if (s === "setup") return "idle";
  return "idle";
}

function titleCase(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function nextLetter(letter: string): string {
  const s = String(letter || "").trim().toUpperCase();
  if (!s) return "A";
  const code = s.charCodeAt(0);
  if (code < 65 || code >= 90) return "A";
  return String.fromCharCode(code + 1);
}

function extractUsedAnswers(payload: any): UsedMove[] {
  const raw =
    payload?.usedAnswers ??
    payload?.used_answers ??
    payload?.state?.usedAnswers ??
    payload?.state?.used_answers ??
    [];

  if (!Array.isArray(raw)) return [];

  return raw
    .map((item: any, index: number) => {
      const source =
        item?.fields && typeof item.fields === "object" ? item.fields : item ?? {};
      const letter = String(
        source.letter ?? source.currentLetter ?? ""
      ).trim().toUpperCase();
      const word = String(
        source.word ?? source.answer ?? source.value ?? ""
      ).trim();
      const by = normalizeTurnOwner(
        source.by ?? source.turnOwner ?? source.owner
      );
      const accepted =
        typeof source.accepted === "boolean" ? source.accepted : true;

      if (!word) return null;

      return {
        letter: letter || String.fromCharCode(65 + index),
        word,
        by,
        accepted,
      } as UsedMove;
    })
    .filter(Boolean) as UsedMove[];
}

function toGameState(payload: any): GameState {
  const source =
    payload?.state && typeof payload.state === "object" ? payload.state : payload ?? {};

  const category = normalizeCategory(
    source.category ?? source.selectedCategory ?? source.gameCategory
  );

  const currentLetter = String(
    source.currentLetter ?? source.letter ?? source.nextLetter ?? "A"
  )
    .trim()
    .toUpperCase() || "A";

  const turnOwner = normalizeTurnOwner(
    source.turnOwner ?? source.owner ?? source.currentTurnOwner
  );

  const startOrder =
    source.startOrder === "assistant" || source.start_order === "assistant"
      ? "assistant"
      : source.startOrder === "user" || source.start_order === "user"
        ? "user"
        : null;

  const usedAnswers = extractUsedAnswers(payload);

  const status = normalizeStatus(source.status);
  const message = String(
    source.message ??
    payload?.message ??
    "Choose a category and begin when you're ready."
  ).trim();

  const messageType =
    source.messageType === "hint" || payload?.messageType === "hint"
      ? "hint"
      : "default";

  const stage: GameStage =
    source.stage === "choose_category" ||
      source.stage === "choose_start_order" ||
      source.stage === "playing" ||
      source.stage === "ended"
      ? source.stage
      : status === "playing"
        ? "playing"
        : "choose_category";

  const mistakeCountThisTurn = Number(source.mistakeCountThisTurn ?? 0) || 0;
  const hintUsedThisTurn = source.hintUsedThisTurn === true;
  const passUsedThisTurn = source.passUsedThisTurn === true;
  const startedAt =
    typeof source.startedAt === "string" && source.startedAt.trim()
      ? source.startedAt.trim()
      : null;

  return {
    category,
    currentLetter,
    turnOwner,
    startOrder,
    usedAnswers,
    status,
    message,
    messageType,
    stage,
    mistakeCountThisTurn,
    hintUsedThisTurn,
    passUsedThisTurn,
    startedAt,
  };
}

function applyArtifactDefaults(
  state: GameState,
  artifact: AbcGameArtifactConfig
): GameState {
  const artifactDefaults = (artifact as any)?.defaults;

  // Important: do not use previewState as an implicit default for live data.
  // Only apply defaults when the artifact explicitly provides them.
  if (!artifactDefaults || typeof artifactDefaults !== "object") {
    return state;
  }

  const looksEmptyOrSetup =
    !state.category &&
    state.usedAnswers.length === 0 &&
    (
      state.stage === "choose_category" ||
      state.stage === "choose_start_order" ||
      state.status === "idle"
    );

  if (!looksEmptyOrSetup) {
    return state;
  }

  const defaultCategory = normalizeCategory(artifactDefaults.category);

  const defaultStartOrder =
    artifactDefaults.startOrder === "assistant" || artifactDefaults.startOrder === "user"
      ? artifactDefaults.startOrder
      : null;

  const defaultTurnOwner = normalizeTurnOwner(
    artifactDefaults.turnOwner ?? defaultStartOrder ?? state.turnOwner
  );

  const defaultStatus = normalizeStatus(artifactDefaults.status ?? state.status);

  const defaultStage: GameStage =
    artifactDefaults.stage === "choose_category" ||
      artifactDefaults.stage === "choose_start_order" ||
      artifactDefaults.stage === "playing" ||
      artifactDefaults.stage === "ended"
      ? artifactDefaults.stage
      : state.stage;

  const defaultMessage =
    typeof artifactDefaults.message === "string" && artifactDefaults.message.trim()
      ? artifactDefaults.message.trim()
      : state.message;

  const defaultCurrentLetter =
    String(artifactDefaults.currentLetter ?? state.currentLetter ?? "A")
      .trim()
      .toUpperCase() || "A";

  return {
    ...state,
    category: defaultCategory || state.category,
    currentLetter: defaultCurrentLetter,
    turnOwner: defaultTurnOwner,
    startOrder: defaultStartOrder,
    status: defaultStatus,
    message: defaultMessage,
    stage: defaultStage,
    startedAt: state.startedAt,
  };
}

const StepAbcGameBubble = forwardRef<
  StepAbcGameBubbleHandle,
  Props
>(
  (
    {
      artifact,
      onAudioResponse,
      onGameMessage,
    },
    ref
  ) => {
    const endpointUrl = N8N_ABC_GAME_URL || artifact.api?.baseUrl || null;
    const accessCode = artifact.context?.accessCode ?? null;
    const sessionId = artifact.context?.sessionId ?? null;

    const outboundType =
      (artifact.context as any)?.responseType ??
      (artifact.context as any)?.outputType ??
      (artifact.context as any)?.audioType ??
      (artifact.context as any)?.type ??
      "text";

    const outboundVoiceId =
      (artifact.context as any)?.voiceId ??
      ((artifact as any)?.config as any)?.voiceId ??
      null;

    const [game, setGame] = useState<GameState>(() =>
      applyArtifactDefaults(previewState, artifact)
    );
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [, setIsLiveData] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const {
      transcript: dictationTranscript,
      isListening: isDictationListening,
      toggle: toggleDictation,
    } = useDictation();

    const dictationModeRef = useRef<number | null>(null);
    const dictationBaseTextRef = useRef("");
    const currentInputModeRef = useRef<"typed" | "dictation">("typed");

    // ------------------------------------------------------------
    // Commit authoritative ABC Game state
    // ------------------------------------------------------------
    //
    // Keep the visual "Buddy says" text and any parent voice
    // presentation synchronized from the exact same GameState.
    const commitGameState = useCallback(
      (nextState: GameState) => {
        setGame(nextState);
        setIsLiveData(true);

        const message =
          String(nextState.message || "").trim();

        if (message) {
          onGameMessage?.(message);
        }
      },
      [onGameMessage]
    );

    const categoryLabel = useMemo(() => {
      return game.category ? titleCase(game.category) : "Not selected";
    }, [game.category]);

    const totalPlayed = game.usedAnswers.length;
    const displayedUsedAnswers = useMemo(
      () => [...game.usedAnswers].reverse(),
      [game.usedAnswers]
    );

    useEffect(() => {
      if (!dictationTranscript) return;
      if (dictationTranscript.sessionId !== dictationModeRef.current) return;

      const dictated = dictationTranscript.text.trim();
      const base = dictationBaseTextRef.current.trim();

      setInput(base && dictated ? `${base} ${dictated}` : dictated || base);
      currentInputModeRef.current = "dictation";
    }, [dictationTranscript]);

    useEffect(() => {
      if (isDictationListening) return;

      dictationModeRef.current = null;
      dictationBaseTextRef.current = "";
    }, [isDictationListening]);

    useEffect(() => {
      if (!isExpanded) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsExpanded(false);
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [isExpanded]);

    const handleToggleDictation = useCallback(() => {
      if (isDictationListening) {
        toggleDictation();
        return;
      }

      dictationBaseTextRef.current = input.trim();

      const sessionId = toggleDictation();
      if (typeof sessionId === "number") {
        dictationModeRef.current = sessionId;
      }
    }, [input, isDictationListening, toggleDictation]);

    const playAudioFromResponse = useCallback(
      async (payload: any) => {
        if (!payload || typeof payload !== "object") return;

        const audioBase64 =
          typeof payload.audioBase64 === "string" ? payload.audioBase64 : null;

        const mimeType =
          typeof payload.mimeType === "string" ? payload.mimeType : "audio/mpeg";

        const audioUrl =
          typeof payload.audioUrl === "string" ? payload.audioUrl : null;

        if (!audioBase64 && !audioUrl) return;

        await onAudioResponse?.(audioBase64, mimeType, audioUrl);
      },
      [onAudioResponse]
    );

    const buildStatePayload = useCallback(() => {
      return {
        category: game.category || null,
        currentLetter: game.currentLetter,
        usedAnswers: game.usedAnswers.map((move) => ({
          letter: move.letter,
          word: move.word,
          by: move.by,
          accepted: move.accepted ?? true,
        })),
        turnOwner: game.turnOwner,
        startOrder: game.startOrder,
        mistakeCountThisTurn: game.mistakeCountThisTurn,
        hintUsedThisTurn: game.hintUsedThisTurn,
        passUsedThisTurn: game.passUsedThisTurn,
        stage: game.stage,
        startedAt: game.startedAt,
        reply: game.message,
        messageType: game.messageType,
      };
    }, [game]);

    const callAction = useCallback(
      async (action: string, extra: Record<string, any> = {}) => {
        if (!endpointUrl || !accessCode) {
          return null;
        }

        console.log("[ABCGame] calling n8n", {
          action,
          accessCode,
          sessionId,
          type: outboundType,
          voiceId: outboundVoiceId,
          inputMode:
            action === "submit_turn" ? currentInputModeRef.current : "widget_event",
          extra,
        });

        const res = await fetch(endpointUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action,
            accessCode,
            sessionId,

            // Preserve normal chat/audio contract for n8n logging + optional TTS.
            type: outboundType,
            responseType: outboundType,
            voiceId: outboundVoiceId,
            inputMode:
              action === "submit_turn" ? currentInputModeRef.current : "widget_event",

            ...extra,
          }),
        });

        if (!res.ok) {
          throw new Error(`Request failed with ${res.status}`);
        }

        const data = await res.json();
        const payload = Array.isArray(data) ? data[0] : data;

        await playAudioFromResponse(payload);

        return payload;
      },
      [
        endpointUrl,
        accessCode,
        sessionId,
        outboundType,
        outboundVoiceId,
        playAudioFromResponse,
      ]
    );

    const loadGame = useCallback(async () => {
      if (!endpointUrl || !accessCode) {
        setIsLiveData(false);
        setGame(applyArtifactDefaults(previewState, artifact));
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await callAction("get_state");
        if (!data) {
          setIsLiveData(false);
          setGame(applyArtifactDefaults(previewState, artifact));
          return;
        }

        const loadedState = toGameState(data);

        const isFreshGame =
          !loadedState.category &&
          loadedState.usedAnswers.length === 0 &&
          loadedState.status === "idle" &&
          (
            loadedState.stage === "choose_category" ||
            loadedState.stage === "choose_start_order"
          );

        if (isFreshGame) {
          const startedData = await callAction("start", {
            category: "animals",
            startOrder: "user",
          });

          const nextState =
            startedData
              ? toGameState(startedData)
              : previewState;

          commitGameState(nextState);
        } else {
          // Preserve any existing or in-progress game.
          commitGameState(loadedState);
        }
      } catch (err) {
        console.error("[ABCGame] load failed:", err);
        setError("Could not load ABC Game.");
        setIsLiveData(false);
        setGame(applyArtifactDefaults(previewState, artifact));
      } finally {
        setLoading(false);
      }
    }, [
      endpointUrl,
      accessCode,
      callAction,
      artifact,
      commitGameState,
    ]);

    useEffect(() => {
      if (!endpointUrl || !accessCode) {
        return;
      }
    
      let cancelled = false;
    
      // Defer one tick so React StrictMode's temporary
      // development mount can clean itself up before
      // we perform the real ABC Game load.
      const timerId = window.setTimeout(() => {
        if (cancelled) {
          return;
        }
    
        void loadGame();
      }, 0);
    
      return () => {
        cancelled = true;
        window.clearTimeout(timerId);
      };
    }, [
      endpointUrl,
      accessCode,
      sessionId,
      loadGame,
    ]);

    const handleSelectCategory = async (category: Category) => {
      if (!category) return;

      // Preview/fallback mode only: no backend available.
      // In live mode, n8n owns the screen message and optional audio.
      if (!endpointUrl || !accessCode) {
        setError(null);

        setGame((prev) => {
          return {
            ...prev,
            category,
            startOrder: "user",
            turnOwner: "user",
            stage: "playing",
            status: "playing",
            message: `Great — you start. Name a ${category === "food" ? "food" : "animal"
              } that starts with A.`,
            messageType: "default",
            currentLetter: "A",
            usedAnswers: [],
            mistakeCountThisTurn: 0,
            hintUsedThisTurn: false,
            passUsedThisTurn: false,
            startedAt: new Date().toISOString(),
          };
        });

        return;
      }

      setActing(true);
      setError(null);

      try {
        const data = await callAction("start", {
          category,
          startOrder: "user",
        });

        if (data) {
          commitGameState(
            toGameState(data)
          );
        }
      } catch (err) {
        console.error("[ABCGame] category selection failed:", err);
        setError("Could not select that category.");
      } finally {
        setActing(false);
      }
    };

    const handleSelectStartOrder = async (startOrder: TurnOwner) => {
      if (!game.category) return;

      if (!endpointUrl || !accessCode) {
        setGame({
          category: game.category,
          currentLetter: "A",
          turnOwner: startOrder,
          startOrder,
          usedAnswers: [],
          status: "playing",
          message:
            startOrder === "user"
              ? `Great — we’ll play food, and you’ll go first. Name a ${game.category === "food" ? "food" : "animal"} that starts with A.`
              : `Great — Buddy starts.`,
          messageType: "default",
          stage: "playing",
          mistakeCountThisTurn: 0,
          hintUsedThisTurn: false,
          passUsedThisTurn: false,
          startedAt: new Date().toISOString(),
        });
        setError(null);
        return;
      }

      setActing(true);
      setError(null);

      try {
        const data = await callAction("start", {
          category: game.category,
          startOrder,
        });

        if (data) {
          commitGameState(
            toGameState(data)
          );
        }
      } catch (err) {
        console.error("[ABCGame] start failed:", err);
        setError("Could not start ABC Game.");
      } finally {
        setActing(false);
      }
    };

    const submitTurnAnswer = async (
      rawAnswer: string,
      inputMode: "typed" | "dictation"
    ) => {
      const answer = String(rawAnswer || "").trim();

      if (!answer) return;

      // Do not accept another game turn while the widget
      // is loading, processing, or no longer in play.
      if (
        acting ||
        loading ||
        game.stage !== "playing"
      ) {
        return;
      }

      // If the widget's own dictation mic happens to be
      // listening, stop it before processing this turn.
      if (isDictationListening) {
        toggleDictation();
      }

      currentInputModeRef.current = inputMode;

      // Preview/fallback mode.
      if (!endpointUrl || !accessCode) {
        setGame((prev) => ({
          ...prev,

          usedAnswers: [
            ...prev.usedAnswers,
            {
              letter: prev.currentLetter,
              word: answer,
              by: "user",
              accepted: true,
            },
          ],

          currentLetter:
            nextLetter(prev.currentLetter),

          turnOwner: "assistant",
          status: "playing",

          message:
            `"${answer}" accepted in preview mode. Backend not connected yet.`,

          messageType: "default",
          stage: "playing",
        }));

        setInput("");
        currentInputModeRef.current = "typed";

        return;
      }

      setActing(true);
      setError(null);

      try {
        const data = await callAction(
          "submit_turn",
          {
            answer,
            ...buildStatePayload(),
          }
        );

        if (data) {
          commitGameState(
            toGameState(data)
          );
        }

        setInput("");
        currentInputModeRef.current = "typed";
      } catch (err) {
        console.error(
          "[ABCGame] submit failed:",
          err
        );

        setError(
          "Could not submit that answer."
        );
      } finally {
        setActing(false);
      }
    };

    const handleSubmitTurn = async () => {
      await submitTurnAnswer(
        input,
        currentInputModeRef.current
      );
    };

    const handleInputKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (e.key !== "Enter") return;
      if (e.shiftKey) return;

      e.preventDefault();

      if (acting || loading || !input.trim() || game.stage !== "playing" || isGameEnded) return;

      void handleSubmitTurn();
    };

    const handleHint = async () => {
      if (!endpointUrl || !accessCode) {
        setGame((prev) => ({
          ...prev,
          hintUsedThisTurn: true,
          message: `Preview hint: try a ${prev.category || "category"} word starting with ${prev.currentLetter}.`,
          messageType: "hint",
        }));
        return;
      }

      setActing(true);
      setError(null);

      try {
        const data = await callAction("hint", {
          ...buildStatePayload(),
        });
        if (data) {
          commitGameState(
            toGameState(data)
          );
        }
      } catch (err) {
        console.error("[ABCGame] hint failed:", err);
        setError("Could not get a hint.");
      } finally {
        setActing(false);
      }
    };

    const handlePass = async () => {
      if (!endpointUrl || !accessCode) {
        setGame((prev) => ({
          ...prev,
          turnOwner: prev.turnOwner === "user" ? "assistant" : "user",
          status: "playing",
          passUsedThisTurn: true,
          message: "Turn passed in preview mode.",
          messageType: "default",
        }));
        return;
      }

      setActing(true);
      setError(null);

      try {
        const data = await callAction("pass", {
          ...buildStatePayload(),
        });
        if (data) {
          commitGameState(
            toGameState(data)
          );
        }
      } catch (err) {
        console.error("[ABCGame] pass failed:", err);
        setError("Could not pass this turn.");
      } finally {
        setActing(false);
      }
    };

    const handleQuit = async () => {
      if (!endpointUrl || !accessCode) {
        setGame((prev) => ({
          ...prev,
          status: "quit",
          stage: "ended",
          message: "Game ended in preview mode.",
          messageType: "default",
        }));
        return;
      }

      setActing(true);
      setError(null);

      try {
        const data = await callAction("quit", {
          ...buildStatePayload(),
        });
        if (data) {
          commitGameState(
            toGameState(data)
          );
        }
      } catch (err) {
        console.error("[ABCGame] quit failed:", err);
        setError("Could not quit the game.");
      } finally {
        setActing(false);
      }
    };

    const handleRestart = async () => {
      const nextState: GameState = {
        ...previewState,
        startedAt: null,
      };
    
      setGame(nextState);
    
      setInput("");
      setError(null);
      setIsLiveData(Boolean(endpointUrl && accessCode));
      setIsExpanded(false);
    
      const message =
        String(nextState.message || "").trim();
    
      if (message) {
        onGameMessage?.(message);
      }
    };

    const activeChipClass =
      "border border-indigo-200 bg-indigo-100 text-indigo-700 shadow-sm";
    const inactiveChipClass =
      "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
    const secondaryButtonClass =
      "rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
    const primaryButtonClass =
      "h-11 rounded-2xl bg-indigo-600 px-5 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

    const isQuitLikeMessage =
      /\b(stop|stopped|quit|ended|end the abc game)\b/i.test(game.message);

    const isGameEnded =
      game.status === "quit" ||
      game.stage === "ended" ||
      isQuitLikeMessage;

    // ------------------------------------------------------------
    // External realtime-voice ownership
    // ------------------------------------------------------------
    //
    // While ABC Game is active, it owns realtime voice.
    // Once the game ends, return false so the parent can
    // give voice ownership back to normal chat.
    useImperativeHandle(
      ref,
      () => ({
        executeVoiceCommand: (
          raw: string
        ): boolean => {
          const transcript =
            String(raw || "").trim();

          if (!transcript) {
            return false;
          }

          // Quit button or spoken Quit has already ended
          // the game. Release voice ownership.
          if (isGameEnded) {
            return false;
          }

          // ABC Game is still the active voice owner.
          // Do not allow speech to leak into main chat
          // while a backend action is in progress.
          if (acting || loading) {
            console.info(
              "[ABCGame][Realtime Voice] ignored while busy",
              {
                transcript,
              }
            );

            return true;
          }

          const normalized = transcript
            .toLowerCase()
            .replace(/[’]/g, "'")
            .replace(/[^\w\s']/g, " ")
            .replace(/\s+/g, " ")
            .trim();

          // ------------------------------------------
          // Same actions as the visible game buttons.
          // ------------------------------------------

          const quitCommands = new Set([
            "quit",
            "quit game",
            "quit the game",
            "exit",
            "exit game",
            "stop",
            "stop game",
            "done playing",
            "end game",
            "end the game",
            "cancel",
            "never mind",
          ]);

          if (quitCommands.has(normalized)) {
            void handleQuit();
            return true;
          }

          const hintCommands = new Set([
            "hint",
            "give me a hint",
            "help",
            "clue",
          ]);

          if (hintCommands.has(normalized)) {
            void handleHint();
            return true;
          }

          const passCommands = new Set([
            "pass",
            "skip",
            "i don't know",
            "i dont know",
            "i'm stuck",
            "im stuck",
          ]);

          if (passCommands.has(normalized)) {
            void handlePass();
            return true;
          }

          const restartCommands = new Set([
            "start over",
            "restart",
            "restart game",
            "restart the game",
          ]);

          if (restartCommands.has(normalized)) {
            void handleRestart();
            return true;
          }

          // During setup/loading transitions ABC Game still owns
          // the transcript; do not leak it into normal chat.
          if (game.stage !== "playing") {
            return true;
          }

          // Anything else is the user's game answer.
          //
          // Preserve the existing backend inputMode contract by
          // treating realtime speech as dictation.
          void submitTurnAnswer(
            transcript,
            "dictation"
          );

          return true;
        },
      })
    );

    const buddyMessageTone =
      game.messageType === "hint"
        ? {
          container: "border-emerald-200 bg-emerald-50",
          heading: "text-emerald-900",
          body: "text-emerald-800",
        }
        : isGameEnded
          ? {
            container: "border-amber-200 bg-amber-50",
            heading: "text-amber-900",
            body: "text-amber-800",
          }
          : {
            container: "border-slate-200 bg-slate-50",
            heading: "text-slate-900",
            body: "text-slate-800",
          };
    const renderAbcGameCard = (expandedView = false) => (
      <div
        className={
          expandedView
            ? "flex h-full max-h-[900px] w-full max-w-6xl flex-col overflow-y-auto rounded-2xl bg-white shadow-2xl"
            : "mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        }
      >
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-base font-semibold text-slate-900">
                {artifact.title || artifact.label || "ABC Game"}
              </div>
              <div className="text-sm text-slate-600">
                Practice naming foods or animals in alphabetical order, one letter at a time.
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-indigo-100"
                aria-label={expandedView ? "Minimize ABC Game" : "Maximize ABC Game"}
                title={expandedView ? "Minimize" : "Maximize"}
              >
                {expandedView ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
                {expandedView ? "Minimize" : "Maximize"}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-0 lg:grid-cols-12">
          <div className="min-w-0 border-b border-slate-200 bg-slate-50 p-4 lg:col-span-5 lg:border-b-0 lg:border-r">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                  <div className="text-xs text-slate-500">Category</div>
                  <div className="mt-1 text-sm font-semibold leading-5 text-slate-900 break-words">
                    {categoryLabel}
                  </div>
                </div>

                <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                  <div className="text-xs text-slate-500">Used</div>
                  <div className="mt-1 text-base font-semibold leading-5 text-slate-900">
                    {totalPlayed}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="text-sm font-medium text-slate-700">Choose a category</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={acting || loading}
                    onClick={() => handleSelectCategory("food")}
                    className={[
                      "rounded-full px-3 py-1.5 text-sm font-medium transition",
                      game.category === "food" ? activeChipClass : inactiveChipClass,
                    ].join(" ")}
                  >
                    Food
                  </button>

                  <button
                    type="button"
                    disabled={acting || loading}
                    onClick={() => handleSelectCategory("animals")}
                    className={[
                      "rounded-full px-3 py-1.5 text-sm font-medium transition",
                      game.category === "animals" ? activeChipClass : inactiveChipClass,
                    ].join(" ")}
                  >
                    Animals
                  </button>
                </div>
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="text-sm font-medium text-slate-700">Who should go first?</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={acting || !game.category}
                    onClick={() => handleSelectStartOrder("user")}
                    className={[
                      "rounded-full px-3 py-1.5 text-sm font-medium transition",
                      game.startOrder === "user" ? activeChipClass : inactiveChipClass,
                    ].join(" ")}
                  >
                    I’ll go first
                  </button>

                  <button
                    type="button"
                    disabled={acting || !game.category}
                    onClick={() => handleSelectStartOrder("assistant")}
                    className={[
                      "rounded-full px-3 py-1.5 text-sm font-medium transition",
                      game.startOrder === "assistant" ? activeChipClass : inactiveChipClass,
                    ].join(" ")}
                  >
                    Buddy goes first
                  </button>
                </div>
              </div>

              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="text-sm font-medium text-slate-700">Used answers</div>
                <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                  {displayedUsedAnswers.length > 0 ? (
                    displayedUsedAnswers.map((move, index) => (
                      <div
                        key={`${move.letter}-${move.word}-${index}`}
                        className={[
                          "rounded-xl border-l-4 px-3 py-2",
                          move.by === "user"
                            ? "border-indigo-400 bg-indigo-50/70"
                            : "border-emerald-400 bg-emerald-50/70",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-slate-500">
                              {move.by === "user" ? "You" : "Buddy"}
                            </div>
                            <div className="text-sm font-semibold text-slate-900 break-words">
                              {move.letter} is for {move.word}
                            </div>
                          </div>

                          <span
                            className={[
                              "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                              move.accepted === false
                                ? "border-red-200 bg-red-50 text-red-700"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700",
                            ].join(" ")}
                          >
                            {move.accepted === false ? "Rejected" : "Accepted"}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500">
                      No answers played yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0 p-4 lg:col-span-7">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
                  Current turn: {game.turnOwner === "user" ? "You" : "Buddy"}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
                  Status: {titleCase(game.status)}
                </span>
              </div>

              <div className="rounded-[24px] border border-indigo-100 bg-gradient-to-br from-indigo-50 via-indigo-50/60 to-white p-5 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="grid h-20 w-20 shrink-0 place-items-center rounded-[24px] border border-indigo-200 bg-white text-5xl font-bold text-indigo-600 shadow-sm">
                    {game.currentLetter || "A"}
                  </div>

                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-slate-900">
                      Current category:{" "}
                      <span className="font-medium">{game.category || "not selected"}</span>
                    </div>
                    <div className="mt-1 text-base text-slate-500">
                      Try naming something that starts with this letter.
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={[
                  "rounded-2xl border p-4 transition-colors",
                  buddyMessageTone.container,
                ].join(" ")}
              >
                <div
                  className={[
                    "text-sm font-semibold",
                    buddyMessageTone.heading,
                  ].join(" ")}
                >
                  Buddy says
                </div>
                <div
                  className={[
                    "mt-2 text-sm leading-6",
                    buddyMessageTone.body,
                  ].join(" ")}
                >
                  {loading ? "Loading ABC Game..." : game.message}
                </div>
              </div>

              {error ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              ) : null}

              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-700">Your turn</div>
                <div className="mt-2 text-sm text-slate-600">
                  {game.stage === "playing" ? (
                    <>
                      Enter a {game.category || "category"} word that starts with{" "}
                      <span className="font-semibold text-slate-900">
                        {game.currentLetter || "A"}
                      </span>
                      .
                    </>
                  ) : (
                    <>Choose a category and who should go first before entering an answer.</>
                  )}
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <input
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      currentInputModeRef.current = "typed";
                    }}
                    onKeyDown={handleInputKeyDown}
                    disabled={acting || loading || game.stage !== "playing" || isGameEnded}
                    placeholder={`Type a ${game.category || "category"} word...`}
                    className="h-11 flex-1 rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none placeholder:text-slate-400 shadow-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                  />

                  <button
                    type="button"
                    onClick={handleToggleDictation}
                    disabled={acting || loading || game.stage !== "playing" || isGameEnded}
                    aria-label={isDictationListening ? "Stop dictation" : "Start dictation"}
                    title={isDictationListening ? "Stop dictation" : "Dictate answer"}
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition disabled:cursor-not-allowed disabled:opacity-50 ${isDictationListening
                      ? "bg-red-500 text-white"
                      : "bg-gray-200 text-slate-800 hover:bg-gray-300"
                      }`}
                  >
                    <Mic className="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    onClick={handleSubmitTurn}
                    disabled={acting || loading || !input.trim() || game.stage !== "playing" || isGameEnded}
                    className={primaryButtonClass}
                  >
                    {acting ? "Submitting..." : "Submit turn"}
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleHint}
                    disabled={acting || loading || game.stage !== "playing" || isGameEnded}
                    className={secondaryButtonClass}
                  >
                    Hint
                  </button>

                  <button
                    type="button"
                    onClick={handlePass}
                    disabled={acting || loading || game.stage !== "playing" || isGameEnded}
                    className={secondaryButtonClass}
                  >
                    Pass
                  </button>

                  {artifact.allowRestart !== false ? (
                    <button
                      type="button"
                      onClick={handleRestart}
                      disabled={acting || loading}
                      className={secondaryButtonClass}
                    >
                      Start over
                    </button>
                  ) : null}

                  {artifact.allowQuit !== false ? (
                    <button
                      type="button"
                      onClick={handleQuit}
                      disabled={acting || loading}
                      className="rounded-full border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Quit
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <>
        {renderAbcGameCard(false)}

        {isExpanded && typeof document !== "undefined"
          ? createPortal(
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 p-3 sm:p-5">
              {renderAbcGameCard(true)}
            </div>,
            document.body
          )
          : null}
      </>
    );
  }
);

StepAbcGameBubble.displayName =
  "StepAbcGameBubble";

export default StepAbcGameBubble;